/**
 * The one place the browser half talks to the Worker.
 *
 * Two rules live here rather than at each call site: every request carries the
 * session cookie (`credentials: 'include'`, so a deployment on another origin
 * behaves like this one), and every mutation echoes the CSRF cookie in a header
 * — which is the check `requireCsrf` makes on the other side.
 */
import { assertApiPath, type ApiPath } from './api-path';

export { apiPath, apiQuery, prefix, type ApiPath } from './api-path';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * The whole answer, for the refusals that carry more than a sentence: a 409
     * names the version the change lost to, and a 422 lists every problem by its
     * path (§5.25). `null` when the body was not JSON.
     */
    readonly body: unknown = null
  ) {
    super(message);
  }
}

/**
 * Exported because one thing in the app is not a fetch: the GitHub button is a
 * link the browser follows, and it has to point at the same Worker as the rest.
 */
export const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

/**
 * The CSRF token this browser holds, under either of the two names it can have.
 *
 * The prefixed one first, and that order is the whole of this function. An https
 * deployment writes `__Host-al_csrf` (`cookieName` in the Worker); only
 * `http://localhost` writes the bare name, and a browser that visited before the
 * prefix existed still holds one of those as well.
 *
 * Reading only the bare name meant that on the deployed site — where the prefix
 * is always used — every mutation went out with an empty header and came back
 * 403 *Reload the page and try again*: sign-up, sign-in, checkout, approving a
 * machine, all of it. It looked intermittent because a browser that had visited
 * before the prefix landed still had the legacy cookie to read, and only a fresh
 * one was refused.
 */
function csrfToken(): string {
  const jar = new Map<string, string>();
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key && !jar.has(key)) jar.set(key, rest.join('='));
  }
  return jar.get('__Host-al_csrf') ?? jar.get('al_csrf') ?? '';
}

async function call<T>(method: string, where: Where, body?: unknown, extra: Record<string, string> = {}): Promise<T> {
  // Refused before anything leaves the browser: a path whose segments climb out
  // of where they were put would carry this session's cookie and CSRF header to
  // a route nobody meant to call (0373/api-path-encoded).
  const path = String(where);
  assertApiPath(path);
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(method === 'GET' ? {} : { 'x-airlock-csrf': csrfToken() }),
      ...extra
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    // A 401 from any route means the session this browser thought it had is
    // gone — expired, idled out, or signed out in another tab. It is announced
    // once, here, rather than left to each page: the app was handed its account
    // in the sign-in response and never asks again on its own, so without this
    // the shell went on drawing a signed-in dashboard whose every panel showed
    // "Sign in first" instead of what it was for.
    if (response.status === 401) window.dispatchEvent(new CustomEvent(SIGNED_OUT));
    throw new ApiError(response.status, payload.error ?? 'That did not work.', payload);
  }
  return payload as T;
}

/**
 * The event above. A plain DOM event rather than a callback the app has to
 * thread through every page, because the thing that notices is the fetch and
 * the thing that acts is the shell, and nothing in between needs to know.
 */
export const SIGNED_OUT = 'airlock:signed-out';

/**
 * A path as the calls below take it: a plain literal, or one built by `apiPath`
 * whenever any part of it is a value (0373/api-path-encoded).
 */
type Where = string | ApiPath;

export const api = {
  get: <T>(path: Where): Promise<T> => call<T>('GET', path),
  post: <T>(path: Where, body?: unknown): Promise<T> => call<T>('POST', path, body ?? {}),
  patch: <T>(path: Where, body: unknown): Promise<T> => call<T>('PATCH', path, body),
  del: <T>(path: Where): Promise<T> => call<T>('DELETE', path),
  /**
   * A write with headers of its own — in practice `If-Match`, which every change
   * to an organization's project or settings carries (§5.25): the version the
   * change was made against, so a second person's save is a 409 rather than an
   * overwrite nobody saw.
   */
  send: <T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: Where, body: unknown, headers: Record<string, string>): Promise<T> =>
    call<T>(method, path, body, headers)
};

/** The header a conditional write names its version in. */
export function ifMatch(version: number): Record<string, string> {
  return { 'if-match': String(version) };
}
